/**
 * System Configuration Constants
 *
 * Single source of truth for all magic values used by the system config
 * domain. No logic — pure named constants sourced from env where applicable.
 */

import { RateLimitConfig } from '../types';
import { CONFIG_DEFAULTS } from './configDefaults';
import { CachedGeneralConfig } from '../types/systemConfig.types';

// ── localStorage ──────────────────────────────────────────────────────────────

/**
 * Increment when CachedGeneralConfig shape changes.
 * Triggers automatic eviction of stale entries on next boot.
 */
export const GENERAL_CONFIG_CACHE_VERSION = 1 as const;
export const GENERAL_CONFIG_STORAGE_KEY   = 'sys_config_general' as const;

// ── OCC version keys ──────────────────────────────────────────────────────────

export const RATE_LIMIT_VERSION_KEY = 'system:rate_limits' as const;

// ── Log context tokens ────────────────────────────────────────────────────────

export const SYSTEM_CONFIG_CTX = {
  GET:       'systemConfigCacheService.getStoredGeneralConfig',
  PERSIST:   'systemConfigCacheService.persistGeneralConfig',
  PUB:       'useSystemConfigStore.fetchPublicConfig',
  ADMIN:     'useSystemConfigStore.fetchAdminConfig',
  UPDATE:    'useSystemConfigStore.updateConfig',
  RECONCILE: 'useSystemConfigStore.reconcileWithServerState',
  RL:        'useSystemConfigStore.updateRateLimitConfig',
} as const;

// ── Env-sourced identity defaults ─────────────────────────────────────────────

/**
 * All fallback branding values come from env — zero hardcoded strings.
 * Resolved at module load time via CONFIG_DEFAULTS getters.
 */
export const IDENTITY_DEFAULTS: CachedGeneralConfig = {
  universityName: CONFIG_DEFAULTS.SYSTEM_IDENTITY.UNIVERSITY_NAME,
  systemName:     CONFIG_DEFAULTS.SYSTEM_IDENTITY.SYSTEM_NAME,
  logoUrl:        CONFIG_DEFAULTS.SYSTEM_IDENTITY.LOGO_URL,
  _cacheVersion:  GENERAL_CONFIG_CACHE_VERSION,
};

// ── Rate limit safe defaults ──────────────────────────────────────────────────

/**
 * Conservative fallback values used when the backend rate-limit endpoint
 * is unreachable. All values are sourced as named constants — no magic numbers.
 */
const RL_API_MAX              = 100   as const;
const RL_API_WINDOW_MS        = 60_000   as const;
const RL_LOGIN_MAX            = 10    as const;
const RL_LOGIN_WINDOW_MS      = 60_000   as const;
const RL_AUTH_MAX             = 100   as const;
const RL_AUTH_WINDOW_MS       = 60_000   as const;
const RL_PWD_VERIFY_MAX       = 5     as const;
const RL_PWD_VERIFY_WINDOW_MS = 60_000   as const;
const RL_CONFIG_UPDATE_MAX    = 5     as const;
const RL_CONFIG_UPDATE_MS     = 300_000  as const;
const RL_SMS_MAX              = 20    as const;
const RL_SMS_WINDOW_MS        = 86_400_000 as const;
const RL_OBSERVABILITY_MAX    = 100   as const;
const RL_OBSERVABILITY_MS     = 900_000  as const;

export const INITIAL_RATE_LIMIT_CONFIG: Readonly<RateLimitConfig> = {
  apiMax:                 RL_API_MAX,
  apiWindowMs:            RL_API_WINDOW_MS,
  loginMax:               RL_LOGIN_MAX,
  loginWindowMs:          RL_LOGIN_WINDOW_MS,
  authMax:                RL_AUTH_MAX,
  authWindowMs:           RL_AUTH_WINDOW_MS,
  passwordVerifyMax:      RL_PWD_VERIFY_MAX,
  passwordVerifyWindowMs: RL_PWD_VERIFY_WINDOW_MS,
  configUpdateMax:        RL_CONFIG_UPDATE_MAX,
  configUpdateWindowMs:   RL_CONFIG_UPDATE_MS,
  smsMax:                 RL_SMS_MAX,
  smsWindowMs:            RL_SMS_WINDOW_MS,
  observabilityMax:       RL_OBSERVABILITY_MAX,
  observabilityWindowMs:  RL_OBSERVABILITY_MS,
};
